// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/useObTheme.js
//  MICROSERVICIO ONBOARDING v3 — Control de tema claro/oscuro
//
//  Se apoya en el MISMO mecanismo que ya usa el resto de NSL (LHeader.jsx y
//  Header.jsx): la clave `theme` de localStorage y el atributo `data-theme`
//  sobre <html> y <body>. Así, si el usuario cambia el tema aquí y luego entra
//  a la landing o a la app, se respeta su elección — no hay dos preferencias
//  compitiendo.
//
//  ⚠️  Este archivo NO importa ni modifica esos componentes: solo comparte la
//      convención. El aislamiento del microservicio se mantiene intacto.
//
//  DARK POR DEFECTO: si el usuario nunca eligió, se arranca en oscuro, que es
//  la identidad visual del diagnóstico.
// ═══════════════════════════════════════════════════════════════════════════════

import { useLayoutEffect, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'theme';
const DEFAULT_THEME = 'dark';

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : DEFAULT_THEME;
  } catch {
    // Modo incógnito o storage bloqueado: el flujo sigue en oscuro.
    return DEFAULT_THEME;
  }
}

let theme = readStoredTheme();
const listeners = new Set();

/** Aplica el tema al DOM con la misma convención que el resto de la app. */
function applyToDom(value) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', value);
  document.body.setAttribute('data-theme', value);
}

// ⚠️  NO se aplica al cargar el módulo, a propósito.
//     DiagnosticoPage se importa de forma estática en App.jsx, así que este
//     archivo se evalúa en TODAS las rutas de NSL. Escribir `data-theme` aquí
//     le cambiaría el tema a páginas ajenas al microservicio — LoginPage y
//     RegisterPage, por ejemplo, arrancan en 'light' cuando no hay preferencia
//     guardada, y verían un parpadeo a oscuro antes de corregirse.
//     La escritura al DOM ocurre solo cuando un componente del onboarding se
//     monta (ver useObTheme más abajo).

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return theme;
}

export function setObTheme(value) {
  if (value !== 'light' && value !== 'dark') return;
  theme = value;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Sin persistencia el tema dura lo que dure la sesión: aceptable.
  }
  applyToDom(value);
  listeners.forEach((fn) => fn());
}

export function toggleObTheme() {
  setObTheme(theme === 'dark' ? 'light' : 'dark');
}

/**
 * Lee el tema y lo sincroniza con el DOM mientras haya un componente del
 * onboarding montado.
 *
 * useLayoutEffect (no useEffect) porque corre ANTES de la pintura: el
 * diagnóstico sale ya con su tema y el usuario nunca ve un cambio de color.
 * La escritura es idempotente, así que no importa cuántos componentes del
 * módulo usen el hook a la vez.
 *
 * @returns {'light'|'dark'}
 */
export function useObTheme() {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useLayoutEffect(() => {
    applyToDom(current);
  }, [current]);

  return current;
}
